/**
 * Styled-components implementation of the Pagination component.
 * Composes the styled sub-components from styles.ts. Retained for backward
 * compatibility (system props / styled() extension) via PaginationHybrid.
 */

import { Select } from "@sproutsocial/seeds-react-select";
import { Icon } from "@sproutsocial/seeds-react-icon";
import type { PaginationProps } from "./PaginationTypes";
import { usePaginationModel } from "./usePaginationModel";
import {
  StyledPaginationContainer,
  StyledPaginationControls,
  StyledPaginationInfo,
  StyledPageSizeSelector,
  StyledPageButtons,
  StyledPaginationButton,
} from "./styles";

export const PaginationStyled = (props: PaginationProps) => {
  const { pageSize, totalRows, onPageChange, className } = props;
  const {
    displayType,
    pageSizeOptions,
    tableCountFunction,
    totalPages,
    currentPage,
    startRow,
    endRow,
    canGoPrevious,
    canGoNext,
    handlePrevious,
    handleNext,
    handlePageSizeChange,
    pageNumbers,
  } = usePaginationModel(props);

  if (totalPages <= 1 && !pageSizeOptions) {
    // No pagination needed if only one page and no page size options
    return null;
  }

  return (
    <StyledPaginationContainer className={className}>
      <StyledPaginationInfo>
        {tableCountFunction({
          startNum: startRow,
          endNum: endRow,
          totalRowCount: totalRows,
        })}
      </StyledPaginationInfo>
      {/* Page size selector */}
      {pageSizeOptions && displayType === "dropdown" && (
        <StyledPageSizeSelector>
          <Select
            id="page-size-select"
            name="page-size"
            value={String(pageSize)}
            onChange={handlePageSizeChange}
            ariaLabel="Rows per page"
            size="small"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </Select>
        </StyledPageSizeSelector>
      )}

      {/* TODO: Add inline page size selector for displayType === 'inline' */}

      {/* Page navigation controls */}
      {totalPages > 1 && (
        <StyledPaginationControls>
          <StyledPageButtons>
            <StyledPaginationButton
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              aria-label="Previous page"
            >
              <Icon name="chevron-left-outline" />
            </StyledPaginationButton>

            {pageNumbers.map((pageNum, index) => {
              if (pageNum === "...") {
                return (
                  <StyledPaginationButton
                    key={`ellipsis-${index}`}
                    disabled
                    aria-label="More pages"
                  >
                    <Icon name="ellipsis-horizontal-outline" />
                  </StyledPaginationButton>
                );
              }

              const page = pageNum as number;
              const isCurrentPage = page === currentPage;

              return (
                <StyledPaginationButton
                  key={page}
                  onClick={() => onPageChange(page - 1)}
                  disabled={isCurrentPage}
                  aria-label={`Page ${page}`}
                  aria-current={isCurrentPage ? "page" : undefined}
                  isSelected={isCurrentPage}
                >
                  {page}
                </StyledPaginationButton>
              );
            })}

            <StyledPaginationButton
              onClick={handleNext}
              disabled={!canGoNext}
              aria-label="Next page"
            >
              <Icon name="chevron-right-outline" />
            </StyledPaginationButton>
          </StyledPageButtons>
        </StyledPaginationControls>
      )}
    </StyledPaginationContainer>
  );
};
